/**
 * SoundConfigFlyover — Mode-specific advanced settings for the SoundExpert node.
 *
 * Dismissal and portal rendering are handled by the parent via FlyoutPortal.
 *
 * Content varies by audio mode:
 *   - SFX: Duration (Auto/1s/5s/Custom) + Loop (Off/On)
 *   - Music: Duration (Auto/30s/60s/Custom) + Lyrics (Auto/Custom/Instrumental)
 *   - Speech: Voice profile selector (searchable list), also reused inline
 */

import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  validateCharacterVoiceCatalogueSelection,
  type CharacterVoiceCatalogueEntry,
} from '../../../../api/client'
import { type VoiceProfileDict, type VoiceProfileDictYMap } from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { useVoiceCatalogueModal } from './VoiceCatalogueModalProvider'

// --- Types ------------------------------------------------------------------

export interface SoundConfigFlyoverProps {
  mode: string
  duration: string
  loop: string
  lyricsMode: string
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
}

export interface SoundExpertSpeechVoicePanelProps {
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
  className?: string
}

// --- Constants --------------------------------------------------------------

const SFX_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '1s', value: '1s' },
  { label: '5s', value: '5s' },
  { label: '10s', value: '10s' },
]

const MUSIC_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '30s', value: '30s' },
  { label: '60s', value: '60s' },
  { label: '120s', value: '120s' },
]

const LOOP_OPTIONS = [
  { label: 'Off', value: 'off' },
  { label: 'On', value: 'on' },
]

const LYRICS_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Custom', value: 'custom' },
  { label: 'Instrumental', value: 'instrumental' },
]

/** Map option value → i18n key for translatable labels; missing entries use the raw label. */
const OPTION_LABEL_KEYS: Record<string, string> = {
  auto: 'soundConfigFlyover.auto',
  off: 'soundConfigFlyover.off',
  on: 'soundConfigFlyover.on',
  custom: 'soundConfigFlyover.custom',
  instrumental: 'soundConfigFlyover.instrumental',
}

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'
const voiceProfileLog = createNadDebug('dw:sound-expert-voice-profile')
const WAVEFORM_BARS = [10, 18, 14, 24, 16, 28, 20, 12, 22, 15, 26, 18]

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary/20 text-primary border border-primary/40'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

function toPlainVoiceProfile(
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap,
): VoiceProfileDict | undefined {
  if (!voiceProfile) return undefined
  return {
    provider: voiceProfile.provider,
    voice_id: voiceProfile.voice_id,
    voice_name: voiceProfile.voice_name,
    description: voiceProfile.description,
    voice_prompt: voiceProfile.voice_prompt,
    name: voiceProfile.name,
    audio_sample: voiceProfile.audio_sample,
    speech_text: voiceProfile.speech_text,
    characteristics: voiceProfile.characteristics ? [...voiceProfile.characteristics] : undefined,
    pronunciation_guide: voiceProfile.pronunciation_guide,
    voice_source: voiceProfile.voice_source,
    voice_ref: voiceProfile.voice_ref,
    language: voiceProfile.language,
    locale: voiceProfile.locale,
    use_case: voiceProfile.use_case,
    descriptors: voiceProfile.descriptors ? [...voiceProfile.descriptors] : undefined,
  }
}

function profileFromCatalogueVoice(
  voice: CharacterVoiceCatalogueEntry,
  currentVoiceProfile: VoiceProfileDict | undefined,
): VoiceProfileDict {
  const nextVoiceProfile: VoiceProfileDict = {
    provider: voice.provider,
    voice_id: voice.voice_id,
    voice_name: voice.name,
    description: voice.description,
    name: currentVoiceProfile?.name ?? voice.name,
    voice_source: voice.voice_source,
    voice_ref: voice.voice_ref,
  }
  if (voice.language) nextVoiceProfile.language = voice.language
  if (voice.locale) nextVoiceProfile.locale = voice.locale
  if (voice.use_case) nextVoiceProfile.use_case = voice.use_case
  if (voice.descriptors.length > 0) {
    nextVoiceProfile.descriptors = voice.descriptors
    nextVoiceProfile.characteristics = voice.descriptors
  }
  if (currentVoiceProfile?.pronunciation_guide) {
    nextVoiceProfile.pronunciation_guide = currentVoiceProfile.pronunciation_guide
  }
  if (currentVoiceProfile?.speech_text) {
    nextVoiceProfile.speech_text = currentVoiceProfile.speech_text
  }
  if (currentVoiceProfile?.audio_sample) {
    nextVoiceProfile.audio_sample = currentVoiceProfile.audio_sample
  }
  return nextVoiceProfile
}

// --- Speech Voice Panel -----------------------------------------------------

export const SoundExpertSpeechVoicePanel = memo(function SoundExpertSpeechVoicePanel({
  voiceProfile,
  onVoiceProfileChange,
  className,
}: SoundExpertSpeechVoicePanelProps) {
  const { t } = useTranslation('canvas')
  const [selectionError, setSelectionError] = useState<string | null>(null)
  const { openVoiceCatalogueModal } = useVoiceCatalogueModal()
  const currentVoiceProfile = useMemo(
    () => toPlainVoiceProfile(voiceProfile),
    [voiceProfile],
  )

  const handleCatalogueVoiceSelect = useCallback(
    async (voice: CharacterVoiceCatalogueEntry): Promise<void> => {
      if (!onVoiceProfileChange) {
        throw new Error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
      }
      setSelectionError(null)
      voiceProfileLog({
        event: 'catalogue-select:start',
        voice_id: voice.voice_id,
        voice_ref: voice.voice_ref,
      })
      try {
        await validateCharacterVoiceCatalogueSelection(voice.voice_ref)
        const nextVoiceProfile = profileFromCatalogueVoice(voice, currentVoiceProfile)
        onVoiceProfileChange(nextVoiceProfile)
        voiceProfileLog({
          event: 'catalogue-select:success',
          voice_id: voice.voice_id,
          voice_source: voice.voice_source,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : t('soundConfigFlyover.voiceSelectionFailed')
        setSelectionError(message)
        voiceProfileLog({
          event: 'catalogue-select:error',
          voice_id: voice.voice_id,
          error: message,
        })
        throw new Error(message)
      }
    },
    [currentVoiceProfile, onVoiceProfileChange, t],
  )

  const openCatalogue = useCallback(() => {
    openVoiceCatalogueModal({
      kind: 'sound_expert',
      targetLabel:
        currentVoiceProfile?.voice_name ||
        currentVoiceProfile?.voice_id ||
        t('soundConfigFlyover.voiceProfile'),
      selectedVoiceRef: currentVoiceProfile?.voice_ref,
      onApply: handleCatalogueVoiceSelect,
    })
  }, [currentVoiceProfile, handleCatalogueVoiceSelect, openVoiceCatalogueModal, t])

  const unavailableReason = onVoiceProfileChange
    ? null
    : t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.')

  return (
    <div
      className={`flex flex-col gap-3 ${className ?? 'w-[26rem] max-w-[calc(100vw-2rem)]'}`}
      data-testid="speech-config"
    >
      <label className={LABEL_CLASS}>{t('soundConfigFlyover.voiceProfile')}</label>

      {currentVoiceProfile ? (
        <div
          className="rounded-lg border border-input-border bg-surface-1 p-2"
          data-testid="current-sound-voice-profile"
        >
          <div className="flex items-center gap-2">
            <div
              className="flex h-8 w-12 shrink-0 items-end gap-0.5 rounded-lg border border-input-border bg-input-bg px-1.5 py-1"
              aria-hidden="true"
            >
              {WAVEFORM_BARS.map((height, index) => (
                <span
                  key={`${height}-${index}`}
                  className="w-0.5 rounded-full bg-primary/70"
                  style={{ height }}
                />
              ))}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-foreground">
                {currentVoiceProfile.voice_name || currentVoiceProfile.voice_id || t('voiceTabContent.unnamedVoice')}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {currentVoiceProfile.provider ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {currentVoiceProfile.provider}
                  </span>
                ) : null}
                {currentVoiceProfile.language ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {currentVoiceProfile.language}
                  </span>
                ) : null}
                {currentVoiceProfile.use_case ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {currentVoiceProfile.use_case}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={openCatalogue}
                disabled={Boolean(unavailableReason)}
                title={unavailableReason ?? undefined}
                className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="sound-expert-voice-catalogue-trigger"
              >
                {t('soundConfigFlyover.changeVoice')}
              </button>
              <button
                onClick={() => onVoiceProfileChange?.(undefined)}
                className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3"
                data-testid="clear-voice-btn"
              >
                {t('soundConfigFlyover.clear')}
              </button>
            </div>
          </div>
          {currentVoiceProfile.description ? (
            <p className="mt-2 line-clamp-2 text-xs font-medium leading-relaxed text-text-muted">
              {currentVoiceProfile.description}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-surface-1 px-2 py-2">
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-muted">
            {t('soundConfigFlyover.noVoiceSelected')}
          </span>
          <button
            type="button"
            onClick={openCatalogue}
            disabled={Boolean(unavailableReason)}
            title={unavailableReason ?? undefined}
            className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="sound-expert-voice-catalogue-trigger"
          >
            {t('voiceTabContent.selectVoice', 'Select Voice')}
          </button>
        </div>
      )}
      {selectionError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs font-medium text-foreground">
          {selectionError}
        </p>
      ) : null}
    </div>
  )
})

// --- Component --------------------------------------------------------------

export const SoundConfigFlyover = memo(function SoundConfigFlyover({
  mode,
  duration,
  loop,
  lyricsMode,
  voiceProfile,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onVoiceProfileChange,
}: SoundConfigFlyoverProps) {
  const { t } = useTranslation('canvas')

  const handleDurationClick = useCallback(
    (value: string) => onDurationChange(value),
    [onDurationChange],
  )

  // -- Render SFX config -----------------------------------------------------
  const renderSfxConfig = () => (
    <div className="flex flex-col gap-3" data-testid="sfx-config">
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.duration')}</label>
        <div className="flex gap-1">
          {SFX_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              data-testid={`sfx-duration-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.loop')}</label>
        <div className="flex gap-1">
          {LOOP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLoopChange(opt.value)}
              className={segmentedBtnClass(loop === opt.value)}
              data-testid={`loop-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // -- Render Music config ---------------------------------------------------
  const renderMusicConfig = () => (
    <div className="flex flex-col gap-3" data-testid="music-config">
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.musicDuration')}</label>
        <div className="flex gap-1">
          {MUSIC_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              data-testid={`music-duration-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.lyrics')}</label>
        <div className="flex gap-1">
          {LYRICS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLyricsModeChange(opt.value)}
              className={segmentedBtnClass(lyricsMode === opt.value)}
              data-testid={`lyrics-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const renderSpeechConfig = () => (
    <SoundExpertSpeechVoicePanel
      voiceProfile={voiceProfile}
      onVoiceProfileChange={onVoiceProfileChange}
    />
  )

  return (
    <div data-testid="sound-config-flyover">
      {mode === 'sfx' && renderSfxConfig()}
      {mode === 'music' && renderMusicConfig()}
      {mode === 'speech' && renderSpeechConfig()}
    </div>
  )
})

SoundConfigFlyover.displayName = 'SoundConfigFlyover'
